import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { TIDS } from "@/constants/testIds";
import PasswordInput from "@/components/PasswordInput";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [needs2fa, setNeeds2fa] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, login2fa } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      if (needs2fa) {
        await login2fa(email, password, code);
      } else {
        await login(email, password);
      }
      nav("/dashboard");
    } catch (e2) {
      const detail = e2?.response?.data?.detail;
      // Detect the 2FA challenge marker returned by the backend
      if (
        e2?.response?.status === 401 &&
        typeof detail === "object" &&
        detail?.code === "2fa_required"
      ) {
        setNeeds2fa(true);
        setErr("Two-factor code required");
      } else {
        setErr(typeof detail === "string" ? detail : "Login failed");
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-12">
      <form onSubmit={submit} className="w-full max-w-md sc-card p-8" data-testid="login-form">
        <div className="sc-label text-[#FF3366]">Welcome back</div>
        <h2 className="sc-heading text-3xl font-black mb-6 mt-1">Log in to MystiCast</h2>
        <label htmlFor="login-identifier" className="sc-label block mb-2">Email or username</label>
        <input
          data-testid={TIDS.loginEmail}
          id="login-identifier"
          name="username"
          type="text"
          autoComplete="username"
          value={email}
          onChange={e => setEmail(e.target.value)}
          className="sc-input mb-4"
          required
          disabled={needs2fa}
          placeholder="you@example.com or yourname"
        />
        <label htmlFor="login-password" className="sc-label block mb-2">Password</label>
        <PasswordInput id="login-password" name="password" autoComplete="current-password" data-testid={TIDS.loginPassword} value={password} onChange={e=>setPassword(e.target.value)} className="mb-4" required disabled={needs2fa} />
        {needs2fa && (
          <>
            <label htmlFor="login-2fa-code" className="sc-label block mb-2">6-digit code (or recovery code)</label>
            <input
              data-testid="login-2fa-code"
              id="login-2fa-code"
              name="otp"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={e=>setCode(e.target.value)}
              className="sc-input mb-4 tracking-widest text-lg"
              placeholder="123456"
              autoFocus
              required
            />
          </>
        )}
        {err && <div className="text-[#FF3366] text-xs mb-3" data-testid="login-error">{err}</div>}
        <button data-testid={TIDS.loginSubmit} disabled={busy} className="sc-btn w-full">
          {busy ? "Logging in..." : needs2fa ? "Verify 2FA" : "Log in"}
        </button>
        {needs2fa && (
          <button
            type="button"
            onClick={() => { setNeeds2fa(false); setCode(""); setErr(""); }}
            className="sc-btn-ghost w-full mt-2 text-xs"
            data-testid="login-2fa-cancel"
          >
            Back
          </button>
        )}
        <div className="text-xs text-zinc-400 mt-4 text-center">
          <Link to="/forgot-password" className="text-[#00E5FF] hover:underline" data-testid="login-forgot-link">Forgot password?</Link>
          <span className="mx-2 text-zinc-600">·</span>
          No account? <Link to="/signup" className="text-[#00E5FF] hover:underline">Sign up</Link>
        </div>
      </form>
    </div>
  );
}
